/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import config from '../../../../share/config';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import error from '../../../../common/nodejs/errors';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import util from "../../../../share/util/utils";

/** 各フェーズ定義 */
const PHASE = {
  START: "start", // 待機表示
  SENSOR_VALUE: "sensorValue", // センサ値判定表示
  EXECUTING: "executing", // 実行監視
  SUCCESS: "success", // 停止
  OVER_SPEED_ERROR: "overSpeedError", // 車輪速超過表示
  PRESSURE_SENSOR_ERROR: "pressureSensorError", // 圧力センサ値異常表示
  BRAKE_RELEASE_ERROR: "brakeReleaseError", // ブレーキ解除失敗表示
  BRAKE_FORCE_RELEASE_ERROR: "brakeForceReleaseError", // ブレーキ強制解除失敗表示
  ERROR: "error"
};

/** API type定義 */
const API_TYPE = {
  STANDBY: 'standby',
  READ: 'read',
  SEC_ACCESS: 'sec_access',
  EXECUTE: 'execute',
  TERMINATE: 'terminate',
};

/** Defaultセッション遷移用フラグ */
const SESSION = {
  SESSION: 0,
  COMMON_ERROR: 1
};

/** ルーチン結果 */
const ROUTINE_STATUS = {
  PARAM_0100: "0100",
  PARAM_0101: "0101",
  PARAM_0102: "0102"
};

export default {
  name: "requirement118",
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      displayInfo: "",
      modal_requirement_118: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.OVER_SPEED_ERROR:
        case PHASE.PRESSURE_SENSOR_ERROR:
        case PHASE.BRAKE_RELEASE_ERROR:
        case PHASE.BRAKE_FORCE_RELEASE_ERROR:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.START:
        case PHASE.SENSOR_VALUE:
        case PHASE.EXECUTING:
        default:
          return "secondary";
      }
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_118 = true;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_118 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 終了時の処理 */
    endProcessing() {
      this.closeWizard();
      // 初期画面に戻す
      this.phase = PHASE.START;
    },

    /** 車輪速超過エラー
     * @param {number} waitTime 指定時間
     */
    async overSpeedError(waitTime) {
      await util.sleep(waitTime);
      // 機能終了
      this.endProcessing();
    },

    /** ルーチン結果によって表示内容を変更する
     * @param {String} routineStatus ルーチン結果
     */
    showWording(routineStatus) {
      switch (routineStatus) {
        case ROUTINE_STATUS.PARAM_0100:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_118_TXT_7_0100`);
          break;
        case ROUTINE_STATUS.PARAM_0101:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_118_TXT_7_0101`);
          break;
        case ROUTINE_STATUS.PARAM_0102:
        default:
          this.displayInfo = this.$t(`${this.i18nWsKey}.REQ_WS_118_TXT_7_0102`);
          break;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    // API実行-------------------------------------------
    /** 待機 */
    async doStandby() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.STANDBY,
        };
        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );
        
        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // センサ値判定
              this.phase = PHASE.SENSOR_VALUE;
              await this.doRead();
              break;
            case config.STATUS_WSP_FAILED:
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_118_EXCESSIVE_WHEEL_SPEED:
                  // 車輪速超過表示
                  this.phase = PHASE.OVER_SPEED_ERROR;
                  this.overSpeedError(response.data.user_data.wait_time);
                  break;
                case error.WSP_GENERAL_ERROR:
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** センサ値判定 */
    async doRead() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.READ,
        };
        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // セキュリティアクセス判定
              await this.doSecAccess();
              break;
            case config.STATUS_WSP_FAILED:
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_118_ABNORMAL_PRESSURE_SENSOR_VALUE:
                  // 圧力センサ値異常表示
                  this.phase = PHASE.PRESSURE_SENSOR_ERROR;
                  break;
                case error.WSP_GENERAL_ERROR:
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** セキュリティアクセス判定 */
    async doSecAccess() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.SEC_ACCESS
        };
        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行
              this.phase = PHASE.EXECUTING;
              await this.doExecute();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              // 共通エラー
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行 */
    async doExecute() {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.EXECUTE,
        };
        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 停止(正常終了)
              this.phase = PHASE.SUCCESS;
              break;
            case config.STATUS_WSP_FAILED:
              // 各エラー表示
              switch (response.data.async_code) {
                case error.WSP_118_BRAKE_FORCED_RELEASE_FAILURE:
                  // ブレーキ強制解除失敗表示
                  this.phase = PHASE.BRAKE_FORCE_RELEASE_ERROR;
                  break;
                case error.WSP_118_BRAKE_RELEASE_FAILURE:
                  // ブレーキ解除失敗表示
                  this.phase = PHASE.BRAKE_RELEASE_ERROR;
                  this.showWording(response.data.user_data.routine_status);
                  break;
                case error.WSP_GENERAL_ERROR:
                default:
                  // 該当のエラーコードが存在しない場合、共通エラー
                  // ありえないが定義しておく
                  this.showUnexpectedError();
                  break;
              }
              break;
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了時の処理
     * @param {number} isSession DefaultSession遷移後に遷移があるか
     */
    async doTerminate(isSession) {
      try {
        this.isLoading = true;
        // API実行パラメータ作成
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();
        const requestBody = {
          id_token: token.id_token,
          type: API_TYPE.TERMINATE,
        };
        // 初期処理（要求）
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          requestBody,
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (isSession === SESSION.COMMON_ERROR) {
          // 共通エラーからのDefaultSession遷移後は機能終了
          this.closeWizard();
        } else {
          if (response) {
            switch (response.data.status) {
              case config.STATUS_WSP_COMPLETED:
                // 機能終了
                this.endProcessing();
                break;
              case config.STATUS_WSP_FAILED:
                // 各エラー表示
                // HACK: Phase4Step2-2終了間際にESLintのルールが変更になり、ロジックは変更出来なかったため回避。
                /* eslint-disable max-depth */
                switch (response.data.async_code) {
                  case error.WSP_GENERAL_ERROR:
                  default:
                    // 該当のエラーコードが存在しない場合、共通エラー
                    // ありえないが定義しておく
                    this.showUnexpectedError();
                    break;
                }
                break;
              default:
                this.showUnexpectedError();
                break;
            }
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    // ユーザー操作-------------------------------------------
    /** 共通エラー画面にて「OK」押下 */
    onCommonErrorOkClicked() {
      this.doTerminate(SESSION.COMMON_ERROR);
    },
    /** 待機画面 「OK」押下 */
    onStandbyOkClicked() {
      this.doStandby();
    },
    /** 待機画面 「キャンセル」押下 */
    onStandbyCancelClicked() {
      this.closeWizard();
    },
    /** 停止画面にて「OK」押下 */
    onSuccessOkClicked() {
      this.doTerminate(SESSION.SESSION);
    },
    /** 異常終了画面にて「OK」押下 */
    onErrorOkClicked() {
      this.doTerminate(SESSION.SESSION);
    }
  },

  async mounted() {
    this.init();
  }
};
